/**
 * `VendorLedgerPanel` — the vendor detail "Ledger" tab. A chronological
 * statement of this vendor's bills + live payments with a running balance.
 *
 * Balance sign: **positive = we owe the vendor** (open payable); negative =
 * the vendor holds our advance / credit.
 *
 * @see packages/domain/src/purchases/vendors/get-vendor-ledger.ts
 * @see features/customers/ledger/ledger-page.tsx — the customer-side mirror
 */
import { trpc } from "../../../lib/trpc.ts";
import { formatDate } from "../../../lib/format.ts";

export interface VendorLedgerPanelProps {
  vendorId: string;
  vendorName: string;
}

interface LedgerRow {
  kind: "bill" | "payment";
  entryId: string;
  at: string;
  docNumber: string;
  debitPaise: string;
  creditPaise: string;
  runningBalancePaise: string;
  status: string | null;
}

/** Fetch and render the vendor's bills + payments running statement. */
export function VendorLedgerPanel({ vendorId, vendorName }: VendorLedgerPanelProps) {
  const api = trpc as typeof trpc & Record<string, any>;
  const query = api.vendors.ledger.useQuery({ id: vendorId });

  if (query.isLoading) {
    return <p className="px-1 py-6 text-sm text-muted">Loading the vendor ledger…</p>;
  }
  if (query.error) {
    return (
      <p className="px-1 py-6 text-sm text-bad">
        Couldn&rsquo;t load the ledger. Try reloading the page.
      </p>
    );
  }

  const ledger = query.data as {
    openingBalancePaise: string;
    closingBalancePaise: string;
    rows: LedgerRow[];
  };
  const closing = BigInt(ledger.closingBalancePaise);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-6 rounded-md border border-line bg-raised px-4 py-3">
        <div>
          <p className="text-xs uppercase tracking-wide text-muted">Opening balance</p>
          <p className="font-mono text-sm font-semibold text-ink">
            {formatSignedPaise(ledger.openingBalancePaise)}
          </p>
        </div>
        <div>
          <p className="text-xs uppercase tracking-wide text-muted">Closing balance</p>
          <p
            className={`font-mono text-sm font-semibold ${
              closing > 0n ? "text-bad" : closing < 0n ? "text-ok" : "text-ink"
            }`}
          >
            {formatSignedPaise(ledger.closingBalancePaise)}
          </p>
          <p className="text-xs text-muted">
            {closing > 0n
              ? `we owe ${vendorName}`
              : closing < 0n
                ? "vendor holds our advance"
                : "settled"}
          </p>
        </div>
      </div>

      {ledger.rows.length === 0 ? (
        <div className="rounded-md border border-line px-4 py-10 text-center">
          <p className="font-semibold text-ink">No ledger activity yet</p>
          <p className="mt-1 text-sm text-muted">
            Once this vendor has a bill or a payment, entries appear here in date order with a
            running balance.
          </p>
        </div>
      ) : (
        <div className="overflow-hidden rounded-md border border-line">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[640px] text-sm">
              <thead className="bg-raised text-xs uppercase tracking-wide text-muted">
                <tr>
                  <th className="px-4 py-3 text-left font-medium">Date</th>
                  <th className="px-4 py-3 text-left font-medium">Doc</th>
                  <th className="px-4 py-3 text-left font-medium">Kind</th>
                  <th className="px-4 py-3 text-right font-medium">Debit</th>
                  <th className="px-4 py-3 text-right font-medium">Credit</th>
                  <th className="px-4 py-3 text-right font-medium">Running</th>
                </tr>
              </thead>
              <tbody>
                {ledger.rows.map((row) => {
                  const balance = BigInt(row.runningBalancePaise);
                  const voided = row.status === "void";
                  return (
                    <tr
                      key={`${row.kind}-${row.entryId}`}
                      className={`border-t border-line ${voided ? "text-muted line-through" : "text-ink-2"}`}
                    >
                      <td className="px-4 py-3 font-mono text-muted">{formatDate(row.at)}</td>
                      <td className="px-4 py-3">
                        {row.docNumber || <span className="text-muted">—</span>}
                      </td>
                      <td className="px-4 py-3">{row.kind === "bill" ? "Bill" : "Payment"}</td>
                      <td className="px-4 py-3 text-right font-mono tabular-nums">
                        {row.debitPaise === "0" ? (
                          <span className="text-muted">—</span>
                        ) : (
                          formatPaise(row.debitPaise)
                        )}
                      </td>
                      <td className="px-4 py-3 text-right font-mono tabular-nums">
                        {row.creditPaise === "0" ? (
                          <span className="text-muted">—</span>
                        ) : (
                          formatPaise(row.creditPaise)
                        )}
                      </td>
                      <td
                        className={`px-4 py-3 text-right font-mono font-semibold tabular-nums ${
                          balance < 0n ? "text-ok" : "text-ink"
                        }`}
                      >
                        {formatSignedPaise(row.runningBalancePaise)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

// Local formatters — full commaed rupees so a reconciliation eye can match a
// bank statement (the shared `paiseToInrShort` collapses ≥1L, which a ledger
// must not). Mirrors the customer ledger's local formatters.
function formatPaise(value: string | bigint): string {
  const paise = typeof value === "bigint" ? value : BigInt(value);
  const rupees = Number(paise) / 100;
  return `₹${new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(rupees)}`;
}

function formatSignedPaise(value: string | bigint): string {
  const paise = typeof value === "bigint" ? value : BigInt(value);
  if (paise === 0n) return "₹0";
  const abs = paise < 0n ? -paise : paise;
  const label = formatPaise(abs);
  return paise < 0n ? `−${label}` : label;
}
